import React, { useCallback, useState } from 'react';
import { Box, ChevronDownIcon, ChevronUpIcon, FlatButton, OverflowText, Text } from '@common-fe/common-fe';

import theme from '@/styles/theme';

import { ConnectorVerticalLine, CurvedLine, ExpandButtonBox } from '../HealthPlanSummaryCard.styles';

const LEFT_EXPANDEABLE_MARGIN = '15px';
const CONNECTOR_LINE_HEIGHT = '14px';

interface Props {
  title?: string;
  titleValue?: string;
  titleNode?: React.ReactNode;
  subtitle?: string;
  defaultExpanded?: boolean;
  children?: React.ReactNode;
  testId?: string;
  showArrowDown?: boolean;
  disabled?: boolean;
  isLast?: boolean;
  allowBottomMargin?: boolean;
  hideChildren?: boolean;
  contentLines?: number;
  shouldRenderCurvedLine?: boolean;
  hideConnectorLine?: boolean;
}

const ExpandableRow: React.FC<Props> = ({
  title,
  titleValue,
  titleNode,
  subtitle,
  defaultExpanded = false,
  children,
  testId,
  showArrowDown,
  disabled,
  isLast,
  allowBottomMargin,
  hideChildren,
  contentLines,
  shouldRenderCurvedLine,
  hideConnectorLine,
}) => {
  const [isExpanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    setExpanded((prev) => !prev);
  }, [disabled]);
  return (
    <Box
      data-testid={testId}
      margin={{ bottom: !isLast && isExpanded && allowBottomMargin ? 'spacing12' : undefined }}
    >
      <Box direction="row" align="center" gap="spacing8" style={{ position: 'relative' }}>
        <FlatButton
          onClick={handleToggle}
          data-testid={`${testId}_expand-button`}
          style={{ alignSelf: 'start' }}
        >
          {!hideChildren && shouldRenderCurvedLine && isExpanded && (
            <CurvedLine $contentLines={contentLines} />
          )}
          <ExpandButtonBox $disabled={disabled}>
            {isExpanded ? (
              <ChevronUpIcon size="24px" color={disabled ? 'iconDisabled' : 'iconPrimary'} />
            ) : (
              <ChevronDownIcon size="24px" color={disabled ? 'iconDisabled' : 'iconPrimary'} />
            )}
          </ExpandButtonBox>
        </FlatButton>
        <Box direction="column">
          {titleNode || (
            <Text color="textBody">
              <Text weight={700} color="textBody">
                {title}
              </Text>
              {titleValue && ` ${titleValue}`}
            </Text>
          )}
          {subtitle && <OverflowText title={subtitle} />}
        </Box>
      </Box>
      {!hideChildren && showArrowDown && (
        <Box
          style={{
            position: 'relative',
            minHeight: hideConnectorLine ? undefined : CONNECTOR_LINE_HEIGHT,
            marginLeft: !hideConnectorLine ? LEFT_EXPANDEABLE_MARGIN : 0,
          }}
          height={{ min: hideConnectorLine ? undefined : CONNECTOR_LINE_HEIGHT }}
        >
          {!hideConnectorLine && (
            <ConnectorVerticalLine
              style={
                !isExpanded || !children
                  ? { height: CONNECTOR_LINE_HEIGHT }
                  : { height: `calc(100% + ${theme.spacings.spacing12})` }
              }
            />
          )}
          {isExpanded && children && (
            <Box
              style={{ position: 'relative' }}
              data-testid={`${testId}_content`}
              pad={{ left: !hideConnectorLine ? 'spacing18' : '0px', top: 'spacing12' }}
            >
              {children}
            </Box>
          )}
        </Box>
      )}
      {!hideChildren && !showArrowDown && isExpanded && children && (
        <Box
          style={{
            position: 'relative',
            marginLeft: !hideConnectorLine ? LEFT_EXPANDEABLE_MARGIN : 0,
          }}
        >
          <Box
            style={{ position: 'relative' }}
            data-testid={`${testId}_content`}
            pad={{ left: !hideConnectorLine ? 'spacing18' : '0px', top: 'spacing12' }}
          >
            {children}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ExpandableRow;
